import { Component } from 'preact';
import style from './styles/TextInput.scss';
import SlideDownReveal from '../effects/SlideDownReveal';
import { FormGroup } from 'reactstrap';
import Validator from './Validators';

/**
 * @typedef {Object} OnTextInputChangeEvent
 * @property {string}            name     Name of the input element
 * @property {string}            value    Input value
 * @property {boolean}           valid    Is input element valid?
 * @property {ValidationError[]} errors   A list of errors
 */

/**
 * @typedef {Object} TextInputData
 * @property {string}            value    Input value
 * @property {boolean}           valid    Is input element valid?
 * @property {ValidationError[]} errors   A list of validation errors
 */

/**
 * @typedef {Object} ValidationResult
 * @property {ValidationError[]} errors
 * @property {boolean} valid
 */

/**
 * @typedef {Object} ValidationError
 * @property {string} validator
 * @property {string} msg
 */

/**
 * @property {string} props.name
 * @property {string} props.label
 * @property {boolean} props.initialValidation
 * @property {Function} props.onChange
 * @property {boolean} props.required
 * @property {Validator[]} props.validators
 * @property {Function} props.onValidationChange
 * @property {string} props.value
 */
export default class TextInput extends Component {

	static getDefaultValues() {
		return {
			value: '',
			valid: false,
			errors: [],
			touched: false
		};
	}

	timerId = null;
	nextValue = null;

	state = {
		focused: false
	};

	/**
	 * @return ValidationResult
	 */
	validate = (value) => Validator.validateAll(value, this.props.validators);

	onFocus = () => {
		this.setState({ focused: true });
	};

	onBlur = () => {
		this.setState({ focused: false });
	};

	/**
	 * Validates when input looses focus
	 *
	 * @param {Event} event
	 * @this TextInput
	 */
	onChange = (event) => {
		this.clearOnInputTimer();
		const value = event.target.value;
		this.props.onChangeAction(this.props.name, {
			value,
			...this.validate(value),
			touched: true
		});
	};

	clearOnInputTimer = () => {
		if (this.timerId) {
			clearTimeout(this.timerId);
		}
		this.nextValue = null;
	};

	onInput = (event) => {
		this.clearOnInputTimer();
		const value = event.target.value;
		this.nextValue = value;
		this.timerId = setTimeout(() => {
			this.props.onChangeAction(this.props.name, {
				value,
				...this.validate(value),
				touched: true
			});
		}, this.props.debounce);
	};

	isEmpty = () => this.props.value === '';

	hasValidClass = () => {
		const { parentWasValidated, required, valid, touched } = this.props;
		return (touched || parentWasValidated) && valid && (
			required
				? !this.isEmpty()
				: true
		);
	};

	hasInvalidClass = () => {
		const { parentWasValidated, required, valid, touched } = this.props;
		return (touched || parentWasValidated) && (!valid || (
			required
				? this.isEmpty()
				: false
		));
	};

	constructor(props) {
		const { onChangeAction, value = '', name, touched } = props;
		if (!(onChangeAction instanceof Function)) {
			new Error('TextInput expects onChangeAction prop to be a defined Function.');
		}
		super(props);

		// Run validation for the first time, if requested
		this.props.onChangeAction(name, {
			value,
			...this.validate(value),
			touched
		});
	}

	render({ name, placeholder, autocomplete, required, value = '', valid = true, errors = [], type='text', parentWasValidated, touched }, { focused }) {
		const isEmpty = this.isEmpty();
		const showPlaceholder = !focused && isEmpty;
		const hasValidClass = this.hasValidClass();
		const hasInvalidClass = this.hasInvalidClass();
		const inputValidityClass = hasValidClass ? 'is-valid' : (hasInvalidClass ? 'is-invalid' : '');
		const looksGood = valid && !isEmpty;
		// If we get re-rendered while our timer is pending, we must use the value from timer to avoid artifacts
		if (this.nextValue !== null) {
			value = this.nextValue;
		}
		const useSmallFontSize = placeholder && placeholder.length > 30;
		return (
			<div className="m-3">
				<FormGroup className={style['input-wrapper']}>
					<input className={`form-control ${style.input} ${inputValidityClass}`} {...{ name, type, autocomplete, onInput: this.onInput, onChange: this.onChange, onFocus: this.onFocus, onBlur: this.onBlur, required, value }} />
					<span className={style.placeholder + (showPlaceholder ? ` ${style.visible}` : '') + (useSmallFontSize ? ` ${style.small}` : '')}>{placeholder}</span>
					<SlideDownReveal visible={hasInvalidClass || looksGood} timeout={300} appear>
						{ hasInvalidClass ? (
							<div className="invalid-feedback">
								{ errors.map((error) => <div>{error.msg}</div>) }
							</div>
						) : null }
						{ looksGood ? <div className="valid-feedback">Looks good!</div> : null }
					</SlideDownReveal>
				</FormGroup>
			</div>
		);
	}
}

TextInput.defaultProps = {
	debounce: 500
};



// WEBPACK FOOTER //
// ./components/form/TextInput.js